import { api } from './api';
import { 
  Channel, 
  Video, 
  ApiResponse, 
  PaginatedResponse 
} from '@meradio/shared-types';

export const getFollowedChannels = async (): Promise<Channel[]> => {
  const response = await api.get<Channel[]>('/channels/followed');
  
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get followed channels');
  }
  
  return response.data;
};

export const getChannelDetails = async (channelId: string): Promise<Channel & { videoCount: number; followerCount: number }> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.get<Channel & { videoCount: number; followerCount: number }>(`/channels/${channelId}`);
  
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get channel details');
  }
  
  return response.data;
};

export interface GetChannelVideosParams {
  channelId: string;
  limit?: number;
  offset?: number;
  sortBy?: 'recent' | 'oldest' | 'title';
}

export const getChannelVideos = async ({
  channelId,
  limit = 50,
  offset = 0,
  sortBy = 'recent'
}: GetChannelVideosParams): Promise<PaginatedResponse<Video>> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.get<Video[]>(`/channels/${channelId}/videos`, {
    params: { limit, offset, sortBy }
  });
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to get channel videos');
  }
  
  return response as PaginatedResponse<Video>;
};

export const followChannel = async (channelId: string): Promise<void> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.post(`/channels/${channelId}/follow`);
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to follow channel');
  }
};

export const unfollowChannel = async (channelId: string): Promise<void> => {
  if (!channelId || channelId.trim().length === 0) {
    throw new Error('Channel ID is required');
  }

  const response = await api.delete(`/channels/${channelId}/follow`);
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to unfollow channel');
  }
};

export const isChannelFollowed = async (channelId: string): Promise<boolean> => {
  try {
    const followedChannels = await getFollowedChannels();
    return followedChannels.some(channel => channel.channelId === channelId);
  } catch (error) {
    console.error('Error checking if channel is followed:', error);
    return false;
  }
};

